import { NgClass } from '@angular/common';
import { Component, DestroyRef, LOCALE_ID, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { FeatureFlagsConfigService, LegacyConfigService, StoreConfig, StoreConfigService } from '@scalefast/config-angular';
import {
  CATEGORY_FILTERS_OPTIONS,
  CartProduct,
  CategoryPageFilters,
  Metaproduct,
  Product,
  UpdateWishlistResponse,
  Wishlist,
  createCartProductFromProduct,
} from '@scalefast/core';
import {
  AddToCartService,
  CartService,
  LoginService,
  ProductService,
  ProductsDisplayedAction,
  StaticFileHash,
  WishlistService,
} from '@scalefast/ecommerce-core';
import { CategoryPagePaginationFiltersAdapter, GetProductsByFilterUseCaseAdapter } from '@scalefast/sam';
import { EMPTY, Observable, Subscription, map, switchMap, take } from 'rxjs';
import { AnalyticsStoreService } from 'src/app/shared/services';
import { CustomStoreSearchService } from 'src/app/shared/services/store-search.service';
import { PAGE_DATA } from 'src/types/tokens/inject-tokens';
import { AppStoreProductListComponent } from '../product-list/product-list.component';

import { AppFilterComponent } from 'src/app/shared/components/filter/filter.component';
import { AppPaginationComponent } from 'src/app/shared/components/pagination/pagination.component';

@Component({
  standalone: true,
  selector: 'app-product-grid',
  styleUrls: ['./product-grid.component.scss'],
  templateUrl: './product-grid.component.html',
  imports: [NgClass, AppStoreProductListComponent, StaticFileHash, AppPaginationComponent, AppFilterComponent],
})
export class AppProductGridComponent implements OnInit {
  private featureFlagsConfigService = inject(FeatureFlagsConfigService);
  private analyticsStoreService = inject(AnalyticsStoreService);
  public componentData: any = inject(PAGE_DATA, { optional: true });
  public feedId!: number;

  protected filterService = inject(CustomStoreSearchService);
  protected route = inject(ActivatedRoute);
  protected loginService = inject(LoginService);
  protected cartService = inject(CartService);
  protected productService = inject(ProductService);
  protected wishlistService = inject(WishlistService);
  protected storeConfigService = inject(StoreConfigService);
  protected addToCartService = inject(AddToCartService);
  protected legacyConfigService = inject(LegacyConfigService);
  protected destroyRef = inject(DestroyRef);
  protected locale = inject(LOCALE_ID);
  protected loginURL: string = '';
  protected isLogged: boolean = false;
  protected isLoadMore: boolean = false;
  protected totalProducts: number = 0;
  protected productRequested: Product | undefined;
  protected wishlist: Wishlist | undefined;
  protected isInWishlist = false;
  protected filtersSubscription: Subscription | undefined;
  protected storeConfig: StoreConfig;
  protected metaproductsRequested: Metaproduct[] | undefined;

  public aggregations?: any[] = undefined;
  public productList: Product[] = [];
  public isPoh: boolean = true;
  public colorReferences$: Observable<Record<string, string> | undefined> = EMPTY;
  public colorReferences: Record<string, string> | undefined = {};
  public isFilterOpen: boolean = false;
  public currentOffset: number;
  public pageSize: number;
  public currentPage: number = 1;
  public showNextPage: boolean = false;
  public showLoadMore: boolean = false;
  public currentTemplate: string = 'category';
  public showFilters: boolean = true;

  MAX_ITEM_QUANTITY: number;
  section: string | null = null;

  @ViewChild(AppStoreProductListComponent) storeProductListComponent: AppStoreProductListComponent | undefined;

  constructor() {
    this.feedId = this.componentData.feedId;
    this.showFilters = this.componentData.showFilters ?? true;
    this.pageSize = this.componentData.productsPerPage ?? CATEGORY_FILTERS_OPTIONS.DEFAULT_PAGE_SIZE;
    this.currentOffset = 0;
    this.storeConfig = this.storeConfigService.get();
    this.MAX_ITEM_QUANTITY = this.storeConfig.maxItemQuantity;

    this.loginService.isLogged().subscribe((isLogged: boolean) => (this.isLogged = isLogged));
  }

  ngOnInit(): void {
    this.isPoh = this.feedId == 0 || this.feedId == 71611 || this.feedId == 71638;
    this.analyticsStoreService.pg_content(this.currentTemplate);
    //FIXME: Filters service should not expose a subject, code smell this triggers another subscription in getFilteredProducts
    this.filtersSubscription = this.filterService.filtersSubject
      .asObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filters: CategoryPageFilters) => {
        this.currentOffset = filters.offset;
        this.getFilteredProducts(this.feedId, filters);
      });

    this.filterService.setDefaultFilters();

    this.colorReferences = this.legacyConfigService.get().metacolorReferences;

    this.wishlistService
      .getWishlist$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((wishlist) => {
        this.wishlist = wishlist;
      });
  }

  getFilteredProducts(
    feedId: number,
    filters: CategoryPageFilters = {
      offset: 0,
      pageSize: this.pageSize,
      filters: [],
    },
  ) {
    const action: ProductsDisplayedAction = this.filterService.setEventProductDisplayedAction(filters);
    const filterParams: any = {
      ...this.filterService.getStoreSearchParams(),
      ...{ count: this.pageSize, offset: filters.offset },
      ...GetProductsByFilterUseCaseAdapter.buildParamsFromFilters(feedId, filters),
    };

    if (!this.featureFlagsConfigService.get().filterPOHProductsInSearch && this.isPoh) {
      // If the carousel is for poh see will send the state crit to get the invisible products too
      if (!filterParams.crit || filterParams.crit == 'ALL') {
        filterParams.crit = '';
      }
      if (filterParams.crit && filterParams.crit != '') {
        filterParams.crit += '/';
      }
      filterParams.crit += 'state:INVISIBLE,VALIDATED,CONFIRMED';
    }

    this.filterService
      .getProductsByFilters(filterParams, action)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.aggregations = data.aggregations;
        if (this.isLoadMore) {
          this.productList = this.productList.concat(data.products);
          this.isLoadMore = false;
        } else {
          this.productList = data.products;
        }
        this.totalProducts = data.total;
        this.hasNextPage(data.total);
        this.hasLoadMore(data.total);
      });
  }

  hasNextPage(totalItems: number) {
    if (totalItems <= this.currentOffset + this.pageSize) {
      this.showNextPage = false;
      return;
    }
    this.showNextPage = true;
  }

  hasLoadMore(totalItems: number) {
    if (totalItems <= this.currentOffset + this.pageSize) {
      this.showLoadMore = false;
      return;
    }
    this.showLoadMore = true;
  }

  previousPageEventHandler() {
    this.currentOffset = this.currentOffset - this.pageSize;
    const filters = CategoryPagePaginationFiltersAdapter.buildPartialFiltersFromPagination(this.currentOffset);
    this.filterService.setFilters(filters);
  }

  nextPageEventHandler() {
    this.currentPage = this.currentPage + 1;
    this.currentOffset = this.currentOffset + this.pageSize;
    const filters = CategoryPagePaginationFiltersAdapter.buildPartialFiltersFromPagination(this.currentOffset);
    this.filterService.setFilters(filters);
  }

  loadMoreEventHandler() {
    this.isLoadMore = true;
    this.nextPageEventHandler();
  }

  onGetProductById(productId: string): void {
    this.productService
      .getProduct$(parseInt(productId))
      .pipe(
        map((data: Product) => {
          this.productRequested = data;
          if (this.wishlist) {
            this.isInWishlist = this.wishlist.products.some((product) => productId == product.productID);
          }
        }),
      )
      .subscribe();
  }

  setWishlistedProductEventHandler(product: Product) {
    this.isInWishlist = false;
    this.productRequested = product;

    if (this.wishlist) {
      this.wishlist.products.map((product: any) => {
        if (this.productRequested?.id === product.productID) {
          this.isInWishlist = true;
        }
      });
    }
  }

  getWishlist(): void {
    const wishlist$ = this.wishlistService.getWishlist$();

    wishlist$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((wishlist) => {
      this.wishlist = wishlist;
    });
  }

  addToWishlistEventHandler(): void {
    if (this.wishlist && this.productRequested) {
      this.wishlistService
        .addIntoWishlist$(this.wishlist, this.productRequested)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: UpdateWishlistResponse) => {
          if (response.response_data) {
            this.isInWishlist = true;
            this.getWishlist();
          }
        });
    }
  }

  removeFromWishlistEventHandler(): void {
    if (this.wishlist && this.productRequested) {
      this.wishlistService
        .removeFromWishlist$(this.wishlist, this.productRequested)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((response: UpdateWishlistResponse) => {
          if (response.response_data) {
            this.isInWishlist = false;
            this.getWishlist();
          }
        });
    }
  }

  checkLoggedUserEventHandler(): void {
    this.wishlistService.checkLoggedUser();
  }

  getMetaproducts(productID: string): void {
    this.productService
      .getProduct$(parseInt(productID))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((product: Product) => {
        this.metaproductsRequested = product.metaproductsV4;
      });
  }
}
